import React from "react";
import { CurrencyDollar, DownloadSimple } from "@phosphor-icons/react";
import { ActionButton, StatusBadge } from "../app";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import InvoicePrintView from "./InvoicePrintView";

export function InvoiceDetailDialog({
  invoice,
  open,
  onOpenChange,
  t,
  companyInfo,
  client,
  getClientName,
  getClientRUC,
  onIssue,
  onMarkPaid,
  onEdit,
  onExportPdf,
}) {
  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-xl border-[#242E42] bg-[#151B26]">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-[#F3F4F6]">
              {invoice?.invoice_number}
            </DialogTitle>
          </DialogHeader>
          {invoice && (
            <div className="space-y-4">
              <div className="flex items-start justify-between">
                <span className="text-[#9CA3AF]">{t("client")}</span>
                <div className="text-right">
                  <div
                    className="font-medium text-[#F3F4F6]"
                    data-testid="invoice-detail-client-name"
                  >
                    {getClientName(invoice.client_id)}
                  </div>
                  {getClientRUC(invoice.client_id) ? (
                    <div
                      className="text-xs text-[#9CA3AF]"
                      data-testid="invoice-detail-client-ruc"
                    >
                      RUC: {getClientRUC(invoice.client_id)}
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[#9CA3AF]">{t("status")}</span>
                <StatusBadge value={invoice.status} statusContext="invoice" />
              </div>
              {invoice.quotation_number ? (
                <div className="flex items-center justify-between">
                  <span className="text-[#9CA3AF]">Cotización origen</span>
                  <span className="font-mono text-[#E2B05E]">
                    {invoice.quotation_number}
                  </span>
                </div>
              ) : null}
              {invoice.cufe ? (
                <div className="flex items-start justify-between gap-4">
                  <span className="text-[#9CA3AF]">CUFE</span>
                  <span className="max-w-[70%] break-all text-right font-mono text-xs text-[#F3F4F6]">
                    {invoice.cufe}
                  </span>
                </div>
              ) : null}
              <div className="flex items-center justify-between">
                <span className="text-[#9CA3AF]">{t("issueDate")}</span>
                <span className="text-[#F3F4F6]">{invoice.issue_date}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[#9CA3AF]">{t("dueDate")}</span>
                <span className="text-[#F3F4F6]">{invoice.due_date}</span>
              </div>

              <div className="border-t border-[#242E42] pt-4">
                <h4 className="mb-2 text-sm font-medium text-[#9CA3AF]">Items</h4>
                <div className="space-y-2">
                  {invoice.items?.map((item, index) => (
                    <div key={index} className="flex justify-between text-sm">
                      <span className="text-[#F3F4F6]">
                        {item.description} x{item.quantity}
                      </span>
                      <span className="text-[#F3F4F6]">
                        ${item.total?.toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2 border-t border-[#242E42] pt-4">
                <div className="flex justify-between text-sm">
                  <span className="text-[#9CA3AF]">{t("subtotal")}</span>
                  <span className="text-[#F3F4F6]">
                    ${invoice.subtotal?.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#9CA3AF]">{t("taxAmount")}</span>
                  <span className="text-[#F3F4F6]">
                    ${invoice.tax_amount?.toFixed(2)}
                  </span>
                </div>
                {invoice.tax_retention_applied && (
                  <div className="rounded border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-300">
                    Retención aplicada: ITBMS original{" "}
                    {invoice.tax_retention_original_rate || 0}%, efectivo{" "}
                    {invoice.tax_rate || 0}%.
                  </div>
                )}
                <div className="flex justify-between pt-2">
                  <span className="font-semibold text-[#F3F4F6]">
                    {t("total")}
                  </span>
                  <span className="font-['Outfit'] text-2xl font-bold text-[#E2B05E]">
                    ${invoice.total?.toFixed(2)}
                  </span>
                </div>
              </div>

              {invoice.status === "borrador" && (
                <Button
                  onClick={onIssue}
                  className="w-full bg-blue-500 hover:bg-blue-600"
                >
                  Issue Invoice
                </Button>
              )}
              {invoice.status === "emitida" && (
                <Button
                  onClick={onMarkPaid}
                  className="w-full bg-emerald-500 hover:bg-emerald-600"
                >
                  <CurrencyDollar className="mr-2 h-4 w-4" />
                  Mark as Paid
                </Button>
              )}
              <div className="flex flex-col gap-2 sm:flex-row">
                <ActionButton
                  label="Edit"
                  tone="edit"
                  onClick={onEdit}
                  className="flex-1 border-[#242E42] text-[#9CA3AF] hover:text-[#F3F4F6]"
                />
                <ActionButton
                  icon={DownloadSimple}
                  label="Descargar PDF"
                  tone="warning"
                  onClick={onExportPdf}
                  className="flex-1 border-[#E2B05E]/40 text-[#E2B05E] hover:bg-[#E2B05E]/10"
                  data-testid="download-invoice-pdf-btn"
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {invoice && (
        <div
          style={{
            position: "fixed",
            left: "-10000px",
            top: 0,
            width: "794px",
            pointerEvents: "none",
            visibility: "hidden",
          }}
        >
          <div id="invoice-pdf" style={{ visibility: "visible" }}>
            <InvoicePrintView
              invoice={invoice}
              companyInfo={companyInfo}
              client={client}
              statusLabel={invoice.status}
            />
          </div>
        </div>
      )}
    </>
  );
}

export default InvoiceDetailDialog;
